/**
 * P3-16: LoadoutButton —— 职业配置按钮（迁移自 js/village.js:3709-3827）。
 *
 * 旧实现要点：
 * - 点击：model.options.loadout.setLoadout(true)（按比例分配职业）；
 * - links：editLinks（save/rename/delete）用 addLinkList 渲染为首项显示 + 余项
 *   hover dropdown；pinLink `[v]`/`[ ]` 切换 pinned；
 * - tooltip：getDescription 自定义，含 leaderTrait/leaderJob、各职业占比、
 *   工程师 craft 占比。
 */
import { useCallback, useMemo, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import { makeModernTooltip, useButtonModel } from "../bonfire/useButtonModel";
import type { ModernButtonController } from "../bonfire/useButtonModel";

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

interface LoadoutLinkSnap {
  id: string;
  title: string;
}

interface LoadoutSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: boolean | string;
  editLinks: LoadoutLinkSnap[];
  pinTitle: string;
}

interface LoadoutButtonControllerLike extends ModernButtonController {
  clickHandler(model: unknown): void;
}

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

function toLoadoutSnapshot(model: Record<string, unknown>): LoadoutSnapshot {
  const editLinks = (model.editLinks as { id?: string; title: string }[] | undefined) ?? [];
  const pinLink = model.pinLink as { title?: string } | undefined;
  return {
    name: model.name as string,
    description: (model.description as string) ?? "",
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as boolean | string) ?? "",
    editLinks: editLinks.map((l) => ({ id: l.id ?? l.title, title: l.title })),
    pinTitle: pinLink?.title ?? "",
  };
}

function loadoutSnapshotEq(a: LoadoutSnapshot | null, b: LoadoutSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const linksEq =
    a.editLinks.length === b.editLinks.length &&
    a.editLinks.every((v, i) => v.id === b.editLinks[i].id && v.title === b.editLinks[i].title);
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.visible === b.visible &&
    a.enabled === b.enabled &&
    a.resourceIsLimited === b.resourceIsLimited &&
    linksEq &&
    a.pinTitle === b.pinTitle
  );
}

// ---------------------------------------------------------------------------
// addLinkList dropdown
// ---------------------------------------------------------------------------

interface LinkListItem {
  id: string;
  title: string;
  handler: () => void;
}

function LinkListDropdown({ items }: { items: LinkListItem[] }) {
  const [open, setOpen] = useState(false);
  if (!items.length) {
    return null;
  }
  const [root, ...rest] = items;
  const onLinkClick = (handler: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handler();
  };
  return (
    <div
      style={{ float: "right" }}
      onMouseOver={() => setOpen(true)}
      onMouseOut={() => setOpen(false)}
    >
      <a
        href="#"
        className={`${root.id}Link`}
        style={{ display: "block", float: "right" }}
        title={root.title}
        onClick={onLinkClick(root.handler)}
        dangerouslySetInnerHTML={{ __html: root.title }}
      />
      <div
        className="linkContent"
        style={{
          display: open ? "block" : "none",
          position: "absolute",
          float: "right",
          marginTop: "35px",
          zIndex: 100,
        }}
      >
        {rest.map((item) => (
          <a
            key={item.id}
            href="#"
            className="dropdown-link"
            style={{ display: "block" }}
            title={item.title}
            onClick={onLinkClick(item.handler)}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export interface LoadoutButtonProps {
  loadout: unknown;
}

export function LoadoutButton({ loadout }: LoadoutButtonProps) {
  const controller = useMemo(() => {
    const game = getGame();
    const com = (window as unknown as Record<string, unknown>).com as Record<string, unknown> | undefined;
    const nuclearunicorn = com?.nuclearunicorn as Record<string, unknown> | undefined;
    const gameUi = nuclearunicorn?.game as Record<string, unknown> | undefined;
    const ui = gameUi?.ui as Record<string, unknown> | undefined;
    const Ctor = ui?.LoadoutButtonController as new (game: GamePage) => LoadoutButtonControllerLike;
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game);
  }, []);

  const createOpts = useCallback(
    () => {
      const lo = loadout as { title?: string };
      return {
        name: lo.title ?? "",
        loadout,
      };
    },
    [loadout],
  );

  const { snap, modelRef } = useButtonModel(controller, createOpts, toLoadoutSnapshot, loadoutSnapshotEq);

  const onClick = useCallback(() => {
    controller?.clickHandler?.(modelRef.current);
  }, [controller, modelRef]);

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller, createOpts),
    [controller, createOpts],
  );

  const getSelectedObject = useCallback(() => modelRef.current, [modelRef]);

  if (!snap) {
    return null;
  }

  const makeEditHandler = (index: number) => () => {
    const model = modelRef.current;
    if (!model) {
      return;
    }
    const links = (model.editLinks as { handler?: () => void }[] | undefined) ?? [];
    links[index]?.handler?.();
  };

  const makePinHandler = () => () => {
    const model = modelRef.current;
    if (!model) {
      return;
    }
    const pinLink = model.pinLink as { handler?: () => void } | undefined;
    pinLink?.handler?.();
  };

  const children: ReactNode = (
    <>
      {snap.editLinks.length > 0 && (
        <LinkListDropdown
          items={snap.editLinks.map((link, i) => ({
            id: link.id,
            title: link.title,
            handler: makeEditHandler(i),
          }))}
        />
      )}
      {snap.pinTitle && (
        <a
          href="#"
          className="pinLink"
          style={{ float: "right", paddingLeft: "2px", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            makePinHandler()();
          }}
          dangerouslySetInnerHTML={{ __html: snap.pinTitle }}
        />
      )}
    </>
  );

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={getSelectedObject}
    >
      {children}
    </GameButton>
  );
}
